import React, { useState, useEffect, useCallback } from 'react';
import { getNewsCachedData } from '../utils/marketCache';

interface NewsItem {
  title: string;
  link: string;
  pubDate: string;
  source: string;
  imageUrl?: string;
}

function timeAgo(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  if (isNaN(then)) return '';
  const diff = Math.floor((now - then) / 1000);
  if (diff < 60) return `${diff}초 전`;
  if (diff < 3600) return `${Math.floor(diff / 60)}분 전`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}시간 전`;
  return `${Math.floor(diff / 86400)}일 전`;
}

// ────────────────────────────────────────────
// RSS XML 파서 (DOMParser 사용)
// ────────────────────────────────────────────
function parseRssXml(xml: string, sourceName: string): NewsItem[] {
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(xml, 'text/xml');
    if (doc.querySelector('parsererror')) return [];
    const items = Array.from(doc.querySelectorAll('item'));
    return items.slice(0, 12).map(item => {
      const title = item.querySelector('title')?.textContent?.trim() ?? '';
      // <link> 다음 텍스트 노드 or href 속성
      let link = '';
      const linkEl = item.querySelector('link');
      if (linkEl) {
        link = linkEl.textContent?.trim() || linkEl.getAttribute('href') || '';
      }
      const pubDate = item.querySelector('pubDate')?.textContent?.trim() ?? '';
      const enclosure = item.querySelector('enclosure');
      const mediaThumbnail = item.getElementsByTagName('media:thumbnail')[0];
      const imageUrl =
        mediaThumbnail?.getAttribute('url') ??
        enclosure?.getAttribute('url') ?? undefined;
      return { title, link, pubDate, source: sourceName, imageUrl };
    }).filter(i => i.title && i.link);
  } catch {
    return [];
  }
}

// ────────────────────────────────────────────
// CORS 프록시 3종 순차 시도
// ────────────────────────────────────────────
async function fetchRssText(rssUrl: string): Promise<string> {
  const strategies: Array<() => Promise<string>> = [
    // corsproxy.io — 안정적인 무료 프록시
    async () => {
      const r = await fetch(`https://corsproxy.io/?${encodeURIComponent(rssUrl)}`);
      if (!r.ok) throw new Error(`corsproxy ${r.status}`);
      return r.text();
    },
    // codetabs 프록시
    async () => {
      const r = await fetch(`https://api.codetabs.com/v1/proxy?quest=${encodeURIComponent(rssUrl)}`);
      if (!r.ok) throw new Error(`codetabs ${r.status}`);
      return r.text();
    },
  ];

  for (const fn of strategies) {
    try {
      const text = await fn();
      if (text && text.length > 100) return text;
    } catch (e) {
      console.warn(`[RSS proxy fail] ${rssUrl}`, e);
    }
  }
  throw new Error(`All proxies failed for ${rssUrl}`);
}

async function fetchRssSource(url: string, name: string): Promise<NewsItem[]> {
  try {
    const text = await fetchRssText(url);
    return parseRssXml(text, name);
  } catch (e) {
    console.warn(`[RSS source fail] ${name}:`, e);
    return [];
  }
}

// ────────────────────────────────────────────
// CryptoCompare — KO 우선, 없으면 EN 폴백
// ────────────────────────────────────────────
async function fetchCryptoCompare(): Promise<NewsItem[]> {
  for (const lang of ['KO', 'EN']) {
    try {
      const r = await fetch(
        `https://min-api.cryptocompare.com/data/v2/news/?lang=${lang}&sortOrder=latest`
      );
      if (!r.ok) continue;
      const data = await r.json();
      if (!Array.isArray(data.Data) || data.Data.length === 0) continue;
      const items: NewsItem[] = data.Data.slice(0, 20).map((item: any) => ({
        title: item.title ?? '',
        link: item.url ?? '',
        pubDate: new Date((item.published_on ?? 0) * 1000).toISOString(),
        source: item.source_info?.name ?? 'CryptoCompare',
        imageUrl: item.imageurl ?? undefined,
      })).filter((i: NewsItem) => i.title && i.link);
      if (items.length > 0) return items;
    } catch (e) {
      console.warn(`[CryptoCompare ${lang}]`, e);
    }
  }
  return [];
}

// ────────────────────────────────────────────
// 한국 뉴스 RSS 소스 목록
// ────────────────────────────────────────────
const KO_RSS: { url: string; name: string }[] = [
  { url: 'https://www.coindeskkorea.com/feed/', name: '코인데스크코리아' },
  { url: 'https://www.tokenpost.kr/rss', name: '토큰포스트' },
  { url: 'https://www.blockmedia.co.kr/feed/', name: '블록미디어' },
  { url: 'https://coinreaders.com/rss', name: '코인리더스' },
];

const CACHE_KEY = 'news_page_cache_v6_ko';

async function fetchAllNews(): Promise<NewsItem[]> {
  const cached = getNewsCachedData<NewsItem[]>(CACHE_KEY);
  if (cached && cached.length > 0) return cached;

  // CryptoCompare + 한국 RSS 동시 요청
  const [ccItems, ...rssResults] = await Promise.all([
    fetchCryptoCompare(),
    ...KO_RSS.map(src => fetchRssSource(src.url, src.name)),
  ]);

  const seen = new Set<string>();
  const all: NewsItem[] = [];

  // RSS 우선 (한국어) → CryptoCompare 후순위
  for (const batch of [...rssResults, [ccItems].flat()]) {
    for (const item of batch) {
      if (item.link && !seen.has(item.link)) {
        seen.add(item.link);
        all.push(item);
      }
    }
  }

  all.sort((a, b) => new Date(b.pubDate).getTime() - new Date(a.pubDate).getTime());

  if (all.length > 0) {
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify({ data: all, timestamp: Date.now() }));
    } catch {}
  }

  return all;
}

// ────────────────────────────────────────────
// 컴포넌트
// ────────────────────────────────────────────
const NewsPage: React.FC = () => {
  const [news, setNews] = useState<NewsItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<number | null>(null);
  const [filterSource, setFilterSource] = useState('all');
  const [search, setSearch] = useState('');

  const load = useCallback(async (force = false) => {
    if (force) {
      try { localStorage.removeItem(CACHE_KEY); } catch {}
    }
    setIsLoading(true);
    setError(null);
    try {
      const items = await fetchAllNews();
      setNews(items);
      setLastUpdated(Date.now());
      if (items.length === 0) {
        setError('뉴스를 불러오지 못했습니다. 새로고침을 눌러 다시 시도해주세요.');
      }
    } catch (e) {
      console.error('[NewsPage]', e);
      setError('뉴스 로딩 중 오류가 발생했습니다.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const allSources = [...new Set(news.map(i => i.source))];
  const filtered = news.filter(item => {
    if (filterSource !== 'all' && item.source !== filterSource) return false;
    if (search && !item.title.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="max-w-4xl mx-auto space-y-5">
      {/* 헤더 */}
      <div className="bg-white/40 backdrop-blur-lg border border-white/20 rounded-2xl shadow-lg p-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h2 className="text-xl font-bold text-fuchsia-800/80">📰 암호화폐 뉴스</h2>
            {lastUpdated && (
              <p className="text-xs text-gray-400 mt-0.5">
                {new Date(lastUpdated).toLocaleTimeString('ko-KR')} 기준
                {news.length > 0 && <span className="ml-1.5 text-fuchsia-400">{news.length}건</span>}
              </p>
            )}
          </div>
          <button
            onClick={() => load(true)}
            disabled={isLoading}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg bg-fuchsia-100 text-fuchsia-700 hover:bg-fuchsia-200 disabled:opacity-50 transition-colors"
          >
            {isLoading
              ? <span className="inline-block w-3.5 h-3.5 border-2 border-fuchsia-400 border-t-transparent rounded-full animate-spin" />
              : '🔄'
            } 새로고침
          </button>
        </div>

        {/* 검색 */}
        <div className="flex flex-col sm:flex-row gap-2 mt-4">
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="뉴스 검색..."
            className="flex-1 px-3 py-1.5 text-sm border border-gray-200 rounded-lg bg-white/70 focus:outline-none focus:ring-2 focus:ring-fuchsia-300"
          />
          {/* 소스 필터 */}
          <div className="flex gap-1 flex-wrap">
            <button
              onClick={() => setFilterSource('all')}
              className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${filterSource === 'all' ? 'bg-fuchsia-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
            >
              전체
            </button>
            {allSources.map(src => (
              <button
                key={src}
                onClick={() => setFilterSource(src)}
                className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${filterSource === src ? 'bg-fuchsia-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
              >
                {src}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* 뉴스 목록 */}
      <div className="bg-white/40 backdrop-blur-lg border border-white/20 rounded-2xl shadow-lg overflow-hidden">
        {isLoading && news.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <div className="w-8 h-8 border-2 border-fuchsia-400 border-t-transparent rounded-full animate-spin" />
            <p className="text-gray-400 text-sm">뉴스를 불러오는 중...</p>
          </div>
        ) : error && news.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <p className="text-3xl">📡</p>
            <p className="text-gray-500 text-sm text-center px-4">{error}</p>
            <button
              onClick={() => load(true)}
              className="px-4 py-2 text-sm font-medium rounded-lg bg-fuchsia-100 text-fuchsia-700 hover:bg-fuchsia-200 transition-colors"
            >
              다시 시도
            </button>
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex items-center justify-center py-16">
            <p className="text-gray-400 text-sm">검색 결과가 없습니다.</p>
          </div>
        ) : (
          <div className="divide-y divide-white/30">
            {filtered.map((item, i) => (
              <a
                key={i}
                href={item.link}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-start gap-4 p-4 hover:bg-white/60 transition-colors group"
              >
                {item.imageUrl && (
                  <img
                    src={item.imageUrl}
                    alt=""
                    className="w-16 h-14 object-cover rounded-lg shrink-0 bg-gray-100"
                    onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
                  />
                )}
                <div className="flex-1 min-w-0">
                  <span className="text-xs bg-fuchsia-100 text-fuchsia-600 px-2 py-0.5 rounded-full font-medium">
                    {item.source}
                  </span>
                  <p className="mt-1 text-sm text-gray-800 group-hover:text-fuchsia-700 font-medium leading-snug line-clamp-2 transition-colors">
                    {item.title}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">{timeAgo(item.pubDate)}</p>
                </div>
              </a>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default NewsPage;
